import React, { useState, useRef, FunctionComponent, useImperativeHandle, forwardRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePost, useFetch, notify } from '../../../../components/component-items/helpers'
import { globalState } from '../../../../store'
import { usePermissions } from '../../../../hooks'

// Components
import GlobalStyles from '../../../../components/component-items/styles'
import { Button, Form } from 'react-bootstrap'
import { StatusBoxStyled } from '../../../../components/component-items/status-box'
import GenericModal from '../../../../components/component-items/modal'

// Font Awesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/pro-duotone-svg-icons'

// Cards
import { Condition } from './condition'
import { Actions } from './action'
import { Event } from './event'

// Types
import { defaultFilters } from './types'
import { ButtonGroup } from '../../../../components/component-items/buttons'
import { AutomationPreview } from './preview'

// Utils
import { filterActions, getActionTypes, getDefaultConfigForType } from './utils'

export const Automations = forwardRef((props, ref) => {
  const {
    state: { csrf, userIsWarehouse },
  } = globalState()

  const node = useRef<HTMLFormElement | null>(null)
  const [validated, setValidated] = useState(false)
  const [nameModal, setNameModal] = useState(false)
  const [preview, setPreview] = useState(false)

  const actionTypes = getActionTypes(userIsWarehouse)
  const [automationType, setAutomationType] = useState(actionTypes[0].value)
  const eventDefault = { category: 'now', conditions: '' }
  const [filters, setFilters] = useState<any>(defaultFilters)
  const [event, setEvent] = useState(eventDefault)
  const [actions, setActions] = useState<any>({ ...defaultFilters, type: 'edit_order_field' })

  useImperativeHandle(ref, () => ({
    setExample: ({ type, condition, event, action }: any) => {
      updateType(type)
      setFilters(condition)
      setEvent(event)
      setActions(action)
    },
  }))

  const checkValidity = () => {
    setValidated(true)
    if (node?.current?.checkValidity() === false) {
      return false
    }
    return true
  }

  const handleSubmit = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault()
    e.stopPropagation()
    if (!checkValidity()) {
      return
    }

    if (!confirm('Are you sure you want to run this automation?')) {
      return
    }

    let action = filterActions(actions)

    usePost(
      '/api/core/automation/run/',
      { ...{ category: automationType, condition: filters, event, action } },
      csrf,
      false,
      true,
      true
    ).then((response) => {
      if (response?.error) {
        notify({ type: 'warning', message: response?.error })
      } else {
        notify({ type: 'success', message: 'Automation Ran Successfully' })
      }
    })
  }

  const handlePreview = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault()
    e.stopPropagation()
    if (!checkValidity()) {
      return
    }

    setPreview(true)
    setValidated(false)
  }

  // Use effect when type changes to set default on setFilters
  const updateType = (value: any) => {
    setAutomationType(value)
    const { filters: newFilters, actions: newActions, event: newEvent } = getDefaultConfigForType(value)
    setFilters(newFilters)
    setActions(newActions)
    setEvent(newEvent)
  }

  return (
    <>
      <GlobalStyles.FullPageCard>
        <Form ref={node} validated={validated} onSubmit={handleSubmit} style={{ padding: '0 1em 2em' }}>
          <div style={{ padding: '2em 1em 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '2em' }}>
              <ButtonGroup aria-label="action-types">
                {actionTypes.map(({ label, value }, id: number) => (
                  <Button
                    key={id}
                    variant="secondary"
                    active={automationType == value}
                    onClick={() => updateType(value)}
                  >
                    {label}
                  </Button>
                ))}
              </ButtonGroup>
            </div>
            <h3 style={{ marginTop: '1em' }}>Condition</h3>
            <StatusBoxStyled className={'blue'} style={{ margin: '0 0.9em', textAlign: 'center' }}>
              Please note: Automations apply to all warehouses unless specified.
            </StatusBoxStyled>
            <div style={{ display: 'flex' }}>
              <Condition {...{ automationType, filters, setFilters }} r={240} g={248} b={255} />
            </div>
            <h3 style={{ marginTop: '1em' }}>Trigger Event</h3>
            <div style={{ display: 'flex' }}>
              <Event {...{ automationType, event, setEvent }} />
            </div>
            <h3 style={{ marginTop: '1em' }}>Action</h3>
            {automationType === 'order' && (
              <StatusBoxStyled className={'blue'} style={{ margin: '0 0.9em', textAlign: 'center' }}>
                Please note: all modifications will only be performed on unfulfilled orders.
              </StatusBoxStyled>
            )}
            <Actions {...{ automationType, filters, event, actions, setActions }} r={240} g={248} b={255} />
            <div style={{ padding: '1em 0' }}>
              <GlobalStyles.Button
                type="button"
                className="royal"
                style={{ minWidth: 125, float: 'right' }}
                onClick={() => checkValidity() && setNameModal(true)}
              >
                Save
              </GlobalStyles.Button>
              {event.category === 'now' ? (
                <>
                  <GlobalStyles.Button style={{ minWidth: 125, float: 'right' }} onClick={handleSubmit}>
                    Run
                  </GlobalStyles.Button>
                  <GlobalStyles.Button
                    className="secondary"
                    style={{ minWidth: 125, float: 'right' }}
                    onClick={handlePreview}
                  >
                    Preview
                  </GlobalStyles.Button>
                </>
              ) : null}
            </div>
          </div>
        </Form>
      </GlobalStyles.FullPageCard>
      <AutomationCreate
        {...{ category: automationType, condition: filters, event: event, action: actions }}
        open={nameModal}
        setOpen={setNameModal}
      />
      <AutomationPreview
        {...{
          open: preview,
          setOpen: setPreview,
          category: automationType,
          condition: filters,
          event: event,
          action: actions,
        }}
      />
    </>
  )
})

type AutomationCreateProps = {
  open: boolean
  setOpen: (open: boolean) => void
  category: string
  condition: any
  event: any
  action: any
}
const AutomationCreate: FunctionComponent<AutomationCreateProps> = ({
  open,
  setOpen,
  category,
  condition,
  event,
  action,
}) => {
  const {
    state: { csrf },
  } = globalState()
  const { hasAccess } = usePermissions()
  const hasSettingsAdmin = hasAccess('settings', 'admin')

  const node = useRef<HTMLFormElement | null>(null)
  const [validated, setValidated] = useState(false)
  const navigate = useNavigate()
  const { response: profileData } = useFetch('/api/core/users/profile_data/', {})

  const [name, setName] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)

  const handleSubmit = async (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault()
    e.stopPropagation()
    setValidated(true)
    if (node?.current?.checkValidity() === false) {
      notify({ type: 'error', message: 'Choose a name for this Automation.' })
      return
    }
    const response = await usePost(
      '/api/core/automation/',
      { ...{ category, isPrivate, name, condition, event, action: filterActions(action) } },
      csrf,
      false,
      true,
      true
    )

    // Check if response is a Response object (error case)
    if (typeof (response as any).ok === 'boolean' && typeof (response as any).json === 'function') {
      try {
        const data = await (response as Response).json()
        notify({
          type: 'error',
          message:
            data?.error || data?.detail || data?.name || (response as Response).statusText || 'An error occurred',
        })
      } catch (e) {
        notify({ type: 'error', message: (response as Response).statusText || 'An error occurred' })
      }
      return
    }

    // Check if response has error property (parsed error)
    if (response?.error) {
      notify({ type: 'warning', message: response.error })
      return
    }

    // Success case - reload page
    window.location.reload()
  }

  return (
    <GenericModal
      heading={'Automation Name'}
      show={open}
      onHide={() => setOpen(false)}
      buttons={
        <>
          <GlobalStyles.Button onClick={() => setOpen(false)}>Close</GlobalStyles.Button>
          <GlobalStyles.Button onClick={handleSubmit}>Save</GlobalStyles.Button>
        </>
      }
    >
      <Form ref={node} validated={validated} onSubmit={handleSubmit} style={{ padding: '2em' }}>
        <Form.Control
          type="text"
          name="name"
          placeholder="Example: 'Pause new orders with tag 'hold'"
          value={name}
          required
          onChange={(e) => setName(e.target.value)}
        />
        {hasSettingsAdmin && (
          <GlobalStyles.Tooltip data-title="This will only be visible to executives">
            <div style={{ display: 'flex', marginTop: '1em' }}>
              <Form.Check
                type="checkbox"
                label="Private"
                checked={isPrivate}
                onChange={() => setIsPrivate(!isPrivate)}
              />
              <FontAwesomeIcon
                style={{
                  margin: '0.2em 0.5em',
                }}
                icon={faQuestionCircle}
              />
            </div>
          </GlobalStyles.Tooltip>
        )}
      </Form>
    </GenericModal>
  )
}

Automations.displayName = 'Automations'
